import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type ConfirmRejectModalContextType = {
  reportId: string;
  onOpenConfirmRejectModal: (
    reportId: string,
    rejectSuccessAction?: RejectSuccessAction
  ) => void;
  open: boolean;
  onCloseConfirmRejectModal: () => void;
  rejectSuccessAction: RejectSuccessAction;
};

export enum RejectSuccessAction {
  GOTO_NEXT_TARGET = 'goto_next_target',
  REFRESH_DATA = 'refresh_data',
}

const Context = createContext<ConfirmRejectModalContextType>({
  reportId: '',
  onOpenConfirmRejectModal: () => {},
  open: false,
  onCloseConfirmRejectModal: () => {},
  rejectSuccessAction: RejectSuccessAction.REFRESH_DATA,
});

export const ConfirmRejectModalProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [reportId, setReportId] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [rejectSuccessAction, setRejectSuccessAction] =
    useState<RejectSuccessAction>(RejectSuccessAction.REFRESH_DATA);

  const onOpenConfirmRejectModal = useCallback(
    (
      reportId: string,
      rejectSuccessAction = RejectSuccessAction.REFRESH_DATA
    ) => {
      setReportId(reportId);
      setOpen(true);
      setRejectSuccessAction(rejectSuccessAction);
    },
    []
  );

  const onCloseConfirmRejectModal = useCallback(() => {
    setOpen(false);
    setReportId('');
    setRejectSuccessAction(RejectSuccessAction.REFRESH_DATA);
  }, []);

  const value = useMemo(
    () => ({
      reportId,
      open,
      onOpenConfirmRejectModal,
      onCloseConfirmRejectModal,
      rejectSuccessAction,
    }),
    [
      reportId,
      open,
      onOpenConfirmRejectModal,
      onCloseConfirmRejectModal,
      rejectSuccessAction,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ConfirmRejectModalProvider.displayName = 'ConfirmRejectModalProvider';

export const useConfirmRejectModal = () => useContext(Context);
